import { Download, FileText, Smartphone, BookOpen } from "lucide-react";
import NewsletterForm from "@/components/NewsletterForm";

const RESOURCES_IMG = "/assets/resources-hero.jpg";

const MEDITATIONS = [
  { title: "7 jours pour renouveler ta foi", verse: "Romains 12:2", pages: 12, file: "/resources/meditation-7-jours.pdf" },
  { title: "Prier avec les Psaumes", verse: "Psaume 23", pages: 18, file: "/resources/prier-psaumes.pdf" },
  { title: "Vivre l'Évangile en couple", verse: "Éphésiens 5", pages: 22, file: "/resources/couple-evangile.pdf" },
  { title: "Marcher dans la grâce", verse: "Éphésiens 2:8", pages: 10, file: "/resources/grace-marcher.pdf" },
];

const WALLPAPERS = [
  { verse: "« Sois fort et courageux »", ref: "Josué 1:9", file: "/resources/wallpapers/wallpaper-1.png" },
  { verse: "« L'Éternel est mon berger »", ref: "Psaume 23:1", file: "/resources/wallpapers/wallpaper-2.png" },
  { verse: "« Je peux tout par Christ »", ref: "Philippiens 4:13", file: "/resources/wallpapers/wallpaper-3.png" },
  { verse: "« Aime ton prochain »", ref: "Marc 12:31", file: "/resources/wallpapers/wallpaper-4.png" },
  { verse: "« Ta parole est une lampe »", ref: "Psaume 119:105", file: "/resources/wallpapers/wallpaper-5.png" },
  { verse: "« Sa grâce me suffit »", ref: "2 Corinthiens 12:9", file: "/resources/wallpapers/wallpaper-6.png" },
];

export default function Resources() {
  return (
    <div data-testid="page-resources" className="bg-[#F7F5F0]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={RESOURCES_IMG} alt="Ressources" className="w-full h-full object-cover jt-pan" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2E4959]/90 via-[#4A6D82]/70 to-[#F7F5F0]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-32 md:py-40">
          <span className="text-xs uppercase tracking-[0.3em] text-[#F4E4C1]">Téléchargement gratuit</span>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] text-white mt-4 mb-5 max-w-3xl">
            Nourris <em className="not-italic text-[#F4E4C1]">ta foi</em>.<br /> Au quotidien.
          </h1>
          <p className="text-lg text-white/85 max-w-xl">
            Fiches de méditation à imprimer et fonds d'écran mobiles avec des versets. Choisis, télécharge, marche avec Dieu.
          </p>
        </div>
      </section>

      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-4 h-4 text-[#4A6D82]" strokeWidth={1.75} />
              <span className="text-xs uppercase tracking-[0.3em] text-[#4A6D82]">Méditations</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1E2933]">Fiches PDF</h2>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="meditations-grid">
          {MEDITATIONS.map((m) => (
            <a key={m.title} href={m.file} download data-testid={`meditation-${m.title}`}
              className="group jt-card rounded-2xl bg-white border border-[#DDE3E8] p-6 flex flex-col jt-soft-shadow hover:border-[#4A6D82]">
              <div className="h-14 w-14 rounded-2xl bg-[#E4EBEF] flex items-center justify-center mb-5 group-hover:bg-[#4A6D82] transition-colors">
                <FileText className="w-6 h-6 text-[#4A6D82] group-hover:text-white transition-colors" strokeWidth={1.5} />
              </div>
              <span className="text-xs uppercase tracking-widest text-[#C99A5B] mb-2">{m.verse}</span>
              <h3 className="font-serif text-xl text-[#1E2933] leading-tight mb-3">{m.title}</h3>
              <p className="text-xs text-[#6B7A85] mb-6">PDF</p>
              <span className="mt-auto inline-flex items-center gap-2 text-sm text-[#4A6D82] group-hover:gap-3 transition-all">
                <Download className="w-4 h-4" /> Télécharger
              </span>
            </a>
          ))}
        </div>
      </section>

      <section className="py-20 bg-[#2E4959] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 jt-grain" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Smartphone className="w-4 h-4 text-[#F4E4C1]" strokeWidth={1.75} />
                <span className="text-xs uppercase tracking-[0.3em] text-[#F4E4C1]">Fonds d'écran</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl text-white">Versets mobiles</h2>
            </div>
          </div>

          <div className="grid gap-5 grid-cols-2 md:grid-cols-3 lg:grid-cols-6" data-testid="wallpapers-grid">
            {WALLPAPERS.map((w, i) => (
              <div key={i} className="group jt-card relative aspect-[9/16] rounded-2xl overflow-hidden jt-medium-shadow" data-testid={`wallpaper-${i}`}>
                <img src={w.file} alt={w.verse} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="relative h-full p-4 flex flex-col justify-end text-white">
                  <a
                    href={w.file}
                    download
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/25 backdrop-blur-md border border-white/40 hover:bg-white/40 transition self-start"
                  >
                    <Download className="w-3 h-3" /> Télécharger
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 max-w-4xl mx-auto px-6">
        <NewsletterForm variant="dark" />
      </section>
    </div>
  );
}
